import React, { useEffect, useState } from 'react'
import { Check, Copy, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { EngagementEvidence, EngagementFinding } from '@/lib/engagementsApi'
import { engagementsApi } from '@/lib/engagementsApi'
import { BottomSheetShell } from './BottomSheetShell'
import { SnmpEvidenceCards } from './SnmpEvidenceCards'
import { ServiceEvidenceCards } from './ServiceEvidenceCards'
import {
  displayCommunity,
  extractArp,
  extractAuditSummary,
  extractInterfaces,
  extractRoutes,
  extractSystemInfo,
  humanEvidenceType,
  isSnmpEvidence,
  isSnmpFinding,
  resolveLinkedEvidence,
  snmpCommunityRisk,
  SNMP_RECOMMENDATION,
  snmpFindingSummary,
  snmpFindingTitle,
} from './snmpEvidence'
import { isServiceBannerEvidence, isServiceInventoryEvidence } from './serviceEvidence'

// drawer pops in from the right when a topology node is clicked.
// reads all fields off node.data (Dorothy ships both camelCase and
// snake_case mirror keys, we prefer camelCase since that's the
// original. snake_case is the Phase 2B mirror for selectors)
//
// sections render conditionally, empty ones collapse so a SubnetCloud
// node doesn't show empty OS / Probe / SNMP blocks

interface NodeDetailDrawerProps {
  nodeData: Record<string, any> | null
  onClose: () => void
  // engagement-wide evidence + findings, threaded so the drawer can
  // resolve finding.evidence_ids → real rows and the Host drawer can
  // show a per-host SNMP summary without re-querying the API
  evidenceList?:  EngagementEvidence[]
  findingsList?:  EngagementFinding[]
  // tabs that own the drawer can override what happens when the user
  // jumps from a finding's "Linked evidence" row into the evidence
  // drawer. e.g. EngagementDetailPage swaps nodeData so the same
  // drawer rerenders with the chosen evidence row
  onSelectEvidence?: (ev: EngagementEvidence) => void
  // fired after a successful PATCH from the editable Observed reaction
  // section so the page can refetch and the table/badge update
  onReactionSaved?: (runId: string, observed_reaction: string | null) => void
}

const fmtTime = (iso: string | null | undefined): string | null => {
  if (!iso) return null
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleString()
  } catch {
    return iso
  }
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-xs text-slate-700 break-all">{value}</span>
  </div>
)

const Section: React.FC<{ title: string; children: React.ReactNode; show: boolean }> = ({ title, children, show }) => {
  if (!show) return null
  return (
    <div className="border-t border-slate-100 first:border-t-0 px-4 py-3">
      <h3 className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 font-semibold">{title}</h3>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        {children}
      </div>
    </div>
  )
}

// editable Observed reaction, replaces the prior read-only block.
// manual operator annotation only, never a vulnerability verdict
const OBSERVED_PLACEHOLDER =
  'Example: Target stopped responding during the flood and recovered after the test stopped. No reboot required.'
const OBSERVED_MAX_LEN = 4000

const ObservedReactionEditor: React.FC<{
  engagementId: string | null
  runId:        string | null
  initial:      string | null
  reviewedBy:   string | null
  reviewedAt:   string | null
  onSaved:      (text: string | null) => void
}> = ({ engagementId, runId, initial, reviewedBy, reviewedAt, onSaved }) => {
  const [text, setText]       = useState<string>(initial ?? '')
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(reviewedAt)
  const [savedBy, setSavedBy] = useState<string | null>(reviewedBy)

  // re-sync when the drawer payload changes (e.g. switching runs)
  useEffect(() => {
    setText(initial ?? '')
    setSavedAt(reviewedAt)
    setSavedBy(reviewedBy)
    setError(null)
  }, [initial, reviewedAt, reviewedBy])

  const dirty   = (text.trim() || '') !== (initial?.trim() ?? '')
  const canSave = !!engagementId && !!runId && dirty && !saving && text.length <= OBSERVED_MAX_LEN

  async function handleSave() {
    if (!engagementId || !runId) return
    setError(null)
    setSaving(true)
    try {
      const trimmed = text.trim()
      const payload = trimmed.length === 0 ? null : trimmed
      const row = await engagementsApi.updateAttackReaction(engagementId, runId, payload)
      setSavedAt(row.reviewed_at)
      setSavedBy(row.reviewed_by)
      onSaved(row.observed_reaction)
    } catch (e: any) {
      setError(e?.message ?? 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    setText(initial ?? '')
    setError(null)
  }

  return (
    <div className="border-t border-slate-100 first:border-t-0 px-4 py-3">
      <h3 className="text-[10px] uppercase tracking-wide text-slate-400 mb-1 font-semibold">Observed reaction</h3>
      <p className="text-[11px] text-slate-500 italic mb-2">
        Manual operator note. This is not an automatic vulnerability verdict.
      </p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={OBSERVED_PLACEHOLDER}
        rows={5}
        maxLength={OBSERVED_MAX_LEN}
        disabled={saving}
        className="w-full px-3 py-2 text-xs border border-slate-300 rounded bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
      />
      <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
        <div className="text-[10px] text-slate-400">
          {text.length}/{OBSERVED_MAX_LEN}
          {savedAt && (
            <span className="ml-2">
              · Last edited {fmtTime(savedAt) ?? savedAt}
              {savedBy ? ` by ${savedBy}` : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="text-[11px] px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {error && <p className="text-[11px] text-red-600 mt-2">{error}</p>}
    </div>
  )
}

// host-drawer SNMP block. at-a-glance summary of every SNMP
// evidence/finding row linked to the host, plus a "View" button per
// evidence that swaps the drawer into the full SnmpEvidenceCards view
const HostSnmpSummary: React.FC<{
  evidence: EngagementEvidence[]
  findings: EngagementFinding[]
  onJumpToEvidence?: (ev: EngagementEvidence) => void
}> = ({ evidence, findings, onJumpToEvidence }) => {
  const totals = evidence.reduce((acc, ev) => {
    const audit = extractAuditSummary(ev.raw_json)
    const sys   = extractSystemInfo(ev.raw_json)
    const c     = displayCommunity(audit)
    if (c) acc.communities.add(c)
    if (Array.isArray(audit.valid_communities)) audit.valid_communities.forEach(c => acc.communities.add(c))
    if (audit.enumeration_community) acc.enumeration = audit.enumeration_community
    if (sys.sysName) acc.sysName = sys.sysName
    acc.interfaces += extractInterfaces(ev.raw_json, ev.evidence_type).length
    acc.routes     += extractRoutes(ev.raw_json, ev.evidence_type).length
    acc.arp        += extractArp(ev.raw_json, ev.evidence_type).length
    return acc
  }, {
    communities: new Set<string>(),
    enumeration: null as string | null,
    sysName:     null as string | null,
    interfaces:  0,
    routes:      0,
    arp:         0,
  })
  const topFinding = findings.length > 0 ? findings[0] : null
  return (
    <div className="border-t border-slate-100 px-4 py-3">
      <h3 className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 font-semibold">SNMP</h3>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        {totals.communities.size > 0 && (
          <div className="col-span-2 flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Valid communities</span>
            <span className="flex flex-wrap gap-1">
              {Array.from(totals.communities).map(c => {
                const risk = snmpCommunityRisk(c)
                const tone = risk === 'critical' ? 'bg-rose-100 text-rose-700'
                          : risk === 'high'     ? 'bg-orange-100 text-orange-700'
                          : 'bg-slate-100 text-slate-600'
                return (
                  <span key={c} className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${tone} ${c === totals.enumeration ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}>
                    {c}
                  </span>
                )
              })}
            </span>
          </div>
        )}
        {totals.enumeration && <Field label="Enumeration used" value={<span className="font-mono">{totals.enumeration}</span>} />}
        {totals.sysName     && <Field label="sysName" value={totals.sysName} />}
        {totals.interfaces > 0 && <Field label="Interfaces" value={String(totals.interfaces)} />}
        {totals.routes > 0     && <Field label="Routes" value={String(totals.routes)} />}
        {totals.arp > 0        && <Field label="ARP entries" value={String(totals.arp)} />}
        {topFinding && (
          <div className="col-span-2 text-[11px] text-slate-600">
            <span className="text-slate-400">Finding: </span>
            <span className="font-medium">{topFinding.severity}</span>
            <span className="text-slate-500"> · {topFinding.title}</span>
          </div>
        )}
      </div>
      {topFinding?.recommendation && (
        <div className="mt-2 text-[11px] text-slate-600 leading-relaxed bg-slate-50 rounded p-2">
          {topFinding.recommendation}
        </div>
      )}
      {!topFinding?.recommendation && (
        <div className="mt-2 text-[11px] text-slate-600 leading-relaxed bg-slate-50 rounded p-2">
          {SNMP_RECOMMENDATION}
        </div>
      )}
      {onJumpToEvidence && evidence.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {evidence.slice(0, 4).map(ev => (
            <button
              key={ev.id}
              type="button"
              onClick={() => onJumpToEvidence(ev)}
              className="text-[10px] text-blue-600 hover:underline"
            >
              View {humanEvidenceType(ev.evidence_type)} →
            </button>
          ))}
          {evidence.length > 4 && <span className="text-[10px] text-slate-400">+{evidence.length - 4} more</span>}
        </div>
      )}
    </div>
  )
}

// mini summary of the SNMP evidence backing an SNMP finding. surfaces
// the highlights (valid communities, sysName, table counts) without
// making the user open every linked evidence row separately
const SnmpEvidenceRecap: React.FC<{ linked: EngagementEvidence[] }> = ({ linked }) => {
  const totals = linked.reduce((acc, ev) => {
    const audit = extractAuditSummary(ev.raw_json)
    const sys   = extractSystemInfo(ev.raw_json)
    const c     = displayCommunity(audit)
    if (c) acc.communities.add(c)
    if (Array.isArray(audit.valid_communities)) audit.valid_communities.forEach(c => acc.communities.add(c))
    if (audit.enumeration_community) acc.enumeration = audit.enumeration_community
    if (sys.sysName)     acc.sysName     = sys.sysName
    if (sys.sysObjectID) acc.sysObjectID = sys.sysObjectID
    acc.interfaces += extractInterfaces(ev.raw_json, ev.evidence_type).length
    acc.routes     += extractRoutes(ev.raw_json, ev.evidence_type).length
    acc.arp        += extractArp(ev.raw_json, ev.evidence_type).length
    return acc
  }, {
    communities: new Set<string>(),
    enumeration: null as string | null,
    sysName:     null as string | null,
    sysObjectID: null as string | null,
    interfaces:  0,
    routes:      0,
    arp:         0,
  })
  return (
    <div className="border-t border-slate-100 px-4 py-3">
      <h3 className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 font-semibold">Evidence summary</h3>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        {totals.communities.size > 0 && (
          <div className="col-span-2 flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Valid communities</span>
            <span className="flex flex-wrap gap-1">
              {Array.from(totals.communities).map(c => {
                const risk = snmpCommunityRisk(c)
                const tone = risk === 'critical' ? 'bg-rose-100 text-rose-700'
                          : risk === 'high'     ? 'bg-orange-100 text-orange-700'
                          : 'bg-slate-100 text-slate-600'
                return (
                  <span key={c} className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${tone} ${c === totals.enumeration ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}>
                    {c}
                  </span>
                )
              })}
            </span>
          </div>
        )}
        {totals.enumeration && <Field label="Enumeration used" value={<span className="font-mono">{totals.enumeration}</span>} />}
        {totals.sysName     && <Field label="sysName"          value={totals.sysName} />}
        {totals.sysObjectID && <div className="col-span-2"><Field label="sysObjectID" value={<span className="font-mono text-[10px]">{totals.sysObjectID}</span>} /></div>}
        {totals.interfaces > 0 && <Field label="Interfaces" value={String(totals.interfaces)} />}
        {totals.routes     > 0 && <Field label="Routes"     value={String(totals.routes)} />}
        {totals.arp        > 0 && <Field label="ARP entries" value={String(totals.arp)} />}
      </div>
    </div>
  )
}

// small reusable raw-JSON viewer with a copy-to-clipboard control.
// collapsed by default. expanded state lives in component state so
// the user can leave it open between drawer interactions
const RawJsonBlock: React.FC<{ data: any }> = ({ data }) => {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  if (data == null) return null
  const text = (() => {
    try { return JSON.stringify(data, null, 2) } catch { return String(data) }
  })()
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(text) ; setCopied(true) ; setTimeout(() => setCopied(false), 1500) } catch { /* clipboard denied */ }
  }
  return (
    <div className="border-t border-slate-100 px-4 py-3">
      <div className="flex items-center justify-between mb-1">
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="text-[10px] uppercase tracking-wide text-slate-400 hover:text-slate-600 font-semibold"
        >
          {open ? '▼' : '▶'} Raw JSON
        </button>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-700"
          title="Copy raw JSON"
        >
          {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {open && (
        <pre className="bg-slate-50 p-2 rounded font-mono text-[10px] overflow-x-auto max-h-72 leading-snug">
          {text}
        </pre>
      )}
    </div>
  )
}

export const NodeDetailDrawer: React.FC<NodeDetailDrawerProps> = ({ nodeData, onClose, evidenceList = [], findingsList = [], onSelectEvidence, onReactionSaved }) => {
  const { t } = useTranslation()

  // Esc to close
  useEffect(() => {
    if (!nodeData) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [nodeData, onClose])

  if (!nodeData) return null

  const d = nodeData

  // ── Finding payload (kind:'finding') ────────────────────────────────
  // carries the full asset-summary join the server emits in
  // /api/engagements/:id. when the finding is SNMP-related we resolve
  // the linked evidence rows from the engagement-wide list and render a
  // type-specific summary instead of the generic Classification card
  if (d.kind === 'finding') {
    const sevColor = (s: string): string => {
      switch ((s ?? '').toUpperCase()) {
        case 'CRITICAL': return 'bg-rose-500'
        case 'HIGH':     return 'bg-orange-500'
        case 'MEDIUM':   return 'bg-amber-400'
        case 'LOW':      return 'bg-yellow-300'
        case 'INFO':     return 'bg-sky-300'
        default:         return 'bg-slate-300'
      }
    }
    const findingShim = {
      id: d.id, title: d.title, category: d.category, evidence_ids: d.evidence_ids,
    } as any
    const linked = resolveLinkedEvidence(findingShim, evidenceList)
    const isSnmp = isSnmpFinding(findingShim, evidenceList)
    const displayTitle  = isSnmp ? snmpFindingTitle(d as any, evidenceList) : (d.title ?? 'Finding')
    const summaryText   = isSnmp ? snmpFindingSummary(d as any, evidenceList) : null
    const showRecommendation = d.recommendation ?? (isSnmp ? SNMP_RECOMMENDATION : null)
    return (
      <BottomSheetShell
        open
        onClose={onClose}
        ariaLabel={t('pentest.detail.finding.title', 'Finding details')}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
              <span className={`inline-block w-2 h-2 rounded-full ${sevColor(d.severity)}`} />
              {d.severity ?? 'Finding'}
              {d.status && <span className="text-slate-400">· {d.status}</span>}
            </div>
            <div className="text-base font-semibold text-slate-800 break-words">{displayTitle}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {d.asset && (
          <Section title="Affected host" show>
            {d.asset.host_ip   && <Field label="IP" value={<span className="font-mono">{d.asset.host_ip}</span>} />}
            {d.asset.host_name && <Field label="Hostname" value={d.asset.host_name} />}
            {d.asset.subnet_cidr && <Field label="Subnet" value={<span className="font-mono">{d.asset.subnet_cidr}</span>} />}
            {d.asset.device_type && <Field label="Device type" value={d.asset.device_type} />}
          </Section>
        )}

        {!d.asset && d.external_asset_id && (
          <Section title="Affected host" show>
            <Field label="External asset id" value={<span className="font-mono text-[10px]">{d.external_asset_id}</span>} />
          </Section>
        )}

        {summaryText && (
          <Section title="Summary" show>
            <div className="col-span-2 text-xs text-slate-700 leading-relaxed">{summaryText}</div>
          </Section>
        )}

        {isSnmp && linked.length > 0 && <SnmpEvidenceRecap linked={linked} />}

        {!d.category && !isSnmp && (
          <Section title="Classification" show>
            <Field label="Severity" value={d.severity ?? '—'} />
            <Field label="Status"   value={d.status ?? 'open'} />
          </Section>
        )}
        {d.category && (
          <Section title="Classification" show>
            <Field label="Severity" value={d.severity ?? '—'} />
            <Field label="Status"   value={d.status ?? 'open'} />
            <Field label="Category" value={d.category} />
          </Section>
        )}

        {showRecommendation && (
          <Section title="Recommendation" show>
            <div className="col-span-2 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
              {showRecommendation}
            </div>
          </Section>
        )}

        {/* linked evidence, resolve ids → rows, click swaps drawer */}
        {Array.isArray(d.evidence_ids) && d.evidence_ids.length > 0 && (
          <Section title={`Linked evidence (${linked.length})`} show>
            {linked.length === 0 && (
              <div className="col-span-2 text-[11px] text-slate-400 italic">
                No matching evidence rows for the supplied ids.
              </div>
            )}
            <div className="col-span-2 space-y-1.5">
              {linked.map(ev => (
                <button
                  key={ev.id}
                  type="button"
                  onClick={() => onSelectEvidence
                    ? onSelectEvidence(ev)
                    : undefined}
                  className="w-full text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded px-2 py-1.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-medium text-slate-700">{humanEvidenceType(ev.evidence_type)}</span>
                    {ev.source_tool && <span className="text-[10px] text-slate-400">{ev.source_tool}</span>}
                  </div>
                  {ev.summary && (
                    <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{ev.summary}</div>
                  )}
                  {ev.collected_at && (
                    <div className="text-[10px] text-slate-400 mt-0.5">{fmtTime(ev.collected_at)}</div>
                  )}
                </button>
              ))}
            </div>
          </Section>
        )}

        {d.raw_json && <RawJsonBlock data={d.raw_json} />}
      </BottomSheetShell>
    )
  }

  // ── Evidence payload (kind:'evidence') ──────────────────────────────
  // for Dorothy SNMP evidence we render type-specific cards
  // (SnmpEvidenceCards) above a collapsed raw JSON block. other
  // evidence types stay on the generic Source / Linked host /
  // Summary / Raw JSON layout
  if (d.kind === 'evidence') {
    const evShim = {
      id: d.id, evidence_type: d.evidence_type, raw_json: d.raw_json,
      summary: d.summary, severity_hint: d.severity_hint,
      source_tool: d.source_tool, collected_at: d.collected_at,
      asset: d.asset, asset_id: d.asset_id, external_asset_id: d.external_asset_id,
    } as any as EngagementEvidence
    const snmp = isSnmpEvidence(evShim)
    return (
      <BottomSheetShell
        open
        onClose={onClose}
        ariaLabel={t('pentest.detail.evidence.title', 'Evidence details')}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Evidence</div>
            <div className="text-base font-semibold text-slate-800 break-words">
              {humanEvidenceType(d.evidence_type)}
              {evShim.asset?.host_ip && (
                <span className="ml-2 text-xs font-normal text-slate-500 font-mono">{evShim.asset.host_ip}</span>
              )}
              {d.collected_at && (
                <span className="ml-2 text-[11px] font-normal text-slate-400">{fmtTime(d.collected_at)}</span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {d.asset && (
          <Section title="Linked host" show>
            {d.asset.host_ip   && <Field label="IP" value={<span className="font-mono">{d.asset.host_ip}</span>} />}
            {d.asset.host_name && <Field label="Hostname" value={d.asset.host_name} />}
            {d.asset.subnet_cidr && <Field label="Subnet" value={<span className="font-mono">{d.asset.subnet_cidr}</span>} />}
            {d.asset.device_type && <Field label="Device type" value={d.asset.device_type} />}
          </Section>
        )}

        {snmp ? (
          <SnmpEvidenceCards evidence={evShim} />
        ) : (isServiceBannerEvidence(evShim) || isServiceInventoryEvidence(evShim)) ? (
          <ServiceEvidenceCards evidence={evShim} />
        ) : (
          <>
            <Section title="Source" show>
              <Field label="Type" value={humanEvidenceType(d.evidence_type)} />
              {d.source_tool && <Field label="Tool" value={d.source_tool} />}
              {d.severity_hint && <Field label="Severity" value={d.severity_hint} />}
              {d.collected_at && (
                <div className="col-span-2"><Field label="Collected" value={fmtTime(d.collected_at) ?? d.collected_at} /></div>
              )}
            </Section>
            {d.summary && (
              <Section title="Summary" show>
                <div className="col-span-2 text-xs text-slate-700 whitespace-pre-wrap">{d.summary}</div>
              </Section>
            )}
          </>
        )}

        {/* raw JSON: collapsed by default, copy button. same control
            regardless of evidence type so users always know where to
            find it */}
        {d.raw_json && <RawJsonBlock data={d.raw_json} />}
      </BottomSheetShell>
    )
  }

  // aggregated attack-simulation edge (Attack Path mode bundles N runs
  // of the same type against the same target into one edge). renders
  // group totals plus a per-run table. wording is intentionally factual,
  // "simulation executed", no "succeeded" / "accepted flood"
  if (d.kind === 'attack-group') {
    const fmtNum = (n: number | null | undefined) =>
      n == null ? '—' : new Intl.NumberFormat().format(n)
    const runs = Array.isArray(d.runs) ? d.runs : []
    const totalDuration = typeof d.total_duration === 'number' ? d.total_duration : 0
    const totalPackets  = typeof d.total_packets  === 'number' ? d.total_packets  : 0
    // PPS shown only when both packets and duration are present
    const ppsAvg = totalDuration > 0 ? Math.round(totalPackets / totalDuration) : null
    return (
      <BottomSheetShell
        open
        onClose={onClose}
        ariaLabel={t('pentest.detail.attack.title', 'Attack simulation details')}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Attack simulation</div>
            <div className="text-base font-semibold text-slate-800 truncate">
              {d.attack_type ?? 'Attack simulation'}
              {d.count > 1 && <span className="text-slate-400 ml-1">×{d.count}</span>}
              {d.target_ip && (
                <span className="ml-2 text-xs font-normal text-slate-500 font-mono">{d.target_ip}</span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <Section title="Target" show>
          <Field label="Source" value="Engagement (surveyor)" />
          <Field label="Target" value={<span className="font-mono">{d.target_ip ?? '—'}</span>} />
          {Array.isArray(d.protocols) && d.protocols.length > 0 && (
            <Field label="Protocol" value={d.protocols.join(', ')} />
          )}
        </Section>

        <Section title="Aggregate" show>
          <Field label="Total runs"     value={String(d.count ?? runs.length)} />
          <Field label="Total packets"  value={fmtNum(d.total_packets)} />
          <Field label="Total duration" value={`${fmtNum(d.total_duration)} s`} />
          {ppsAvg != null && <Field label="Avg PPS" value={fmtNum(ppsAvg)} />}
          {d.first_start_time && (
            <div className="col-span-2"><Field label="First start" value={fmtTime(d.first_start_time) ?? d.first_start_time} /></div>
          )}
          {d.last_stop_time && (
            <div className="col-span-2"><Field label="Last stop" value={fmtTime(d.last_stop_time) ?? d.last_stop_time} /></div>
          )}
          <div className="col-span-2 text-[11px] text-slate-500 italic mt-1">
            No observed impact recorded. Attack simulations are test events,
            not automatic vulnerabilities.
          </div>
        </Section>

        {runs.length > 0 && (
          <Section title={`Individual runs (${runs.length})`} show>
            <div className="col-span-2 overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="px-1.5 py-1 font-medium">Started</th>
                    <th className="px-1.5 py-1 font-medium text-right">Pkts</th>
                    <th className="px-1.5 py-1 font-medium text-right">Dur (s)</th>
                    <th className="px-1.5 py-1 font-medium text-right">Mbps</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {runs.map((r: any) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-1.5 py-1">{fmtTime(r.start_time) ?? '—'}</td>
                      <td className="px-1.5 py-1 text-right tabular-nums">{fmtNum(r.packets_sent)}</td>
                      <td className="px-1.5 py-1 text-right tabular-nums">{fmtNum(r.duration_seconds)}</td>
                      <td className="px-1.5 py-1 text-right tabular-nums">{fmtNum(r.target_rate_mbps)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </BottomSheetShell>
    )
  }

  // synthetic "attack" payload from TopologyView attack edges. source
  // and target are cytoscape node ids, we strip the host: prefix for
  // display since the user thinks in IP addresses
  if (d.kind === 'attack') {
    const targetIp = typeof d.target === 'string' ? d.target.replace(/^host:/, '') : d.target
    const sourceLabel = typeof d.source === 'string' && d.source !== 'engagement'
      ? d.source.replace(/^host:/, '')
      : 'Engagement (surveyor)'
    const fmtNum = (n: number | null | undefined) =>
      n == null ? null : new Intl.NumberFormat().format(n)
    return (
      <BottomSheetShell
        open
        onClose={onClose}
        ariaLabel={t('pentest.detail.attack.title', 'Attack simulation details')}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Attack simulation</div>
            <div className="text-sm font-semibold text-slate-800 truncate">{d.attack_type ?? d.label ?? 'Attack simulation'}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
            aria-label={t('common.close', 'Close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <Section title="Target" show>
          <Field label="Source" value={sourceLabel} />
          <Field label="Target" value={<span className="font-mono">{targetIp ?? '—'}</span>} />
          {d.target_port != null && <Field label="Target port" value={String(d.target_port)} />}
          {d.protocol && <Field label="Protocol" value={d.protocol} />}
          {d.source_tool && <Field label="Source tool" value={d.source_tool} />}
        </Section>

        <Section title="Traffic generated" show={!!(d.start_time || d.stop_time || d.duration_seconds != null || d.packets_sent != null || d.target_rate_mbps != null)}>
          {d.target_rate_mbps != null && (
            <Field label="Configured Mbps" value={String(d.target_rate_mbps)} />
          )}
          {d.packets_sent != null && (
            <Field label="Packets sent" value={fmtNum(d.packets_sent) ?? String(d.packets_sent)} />
          )}
          {d.duration_seconds != null && (
            <Field label="Duration (s)" value={String(d.duration_seconds)} />
          )}
          {/* packet-rate telemetry from Dorothy (migration 018).
              Current PPS is only present on actively-streaming records,
              we render it when supplied but don't fake it. Avg PPS
              falls back to packets/duration if Dorothy didn't ship a
              measurement. Peak / Target stay null otherwise */}
          {d.current_pps != null && (
            <Field label="Current PPS" value={fmtNum(d.current_pps) ?? String(d.current_pps)} />
          )}
          {(() => {
            const fromDorothy = d.average_pps != null && isFinite(d.average_pps)
            const avg = fromDorothy
              ? d.average_pps
              : (d.approx_pps != null
                  ? d.approx_pps
                  : (d.packets_sent != null && d.duration_seconds != null && d.duration_seconds > 0
                      ? Math.round(d.packets_sent / d.duration_seconds)
                      : null))
            if (avg == null) return null
            return (
              <Field
                label={`Average PPS${fromDorothy ? '' : ' (calculated)'}`}
                value={
                  <span title={fromDorothy ? 'Reported by Dorothy' : 'Approximated from packets ÷ duration'}>
                    {fmtNum(avg) ?? String(avg)}
                  </span>
                }
              />
            )
          })()}
          {d.peak_pps != null && (
            <Field label="Peak PPS" value={fmtNum(d.peak_pps) ?? String(d.peak_pps)} />
          )}
          {d.target_pps != null && (
            <Field label="Target PPS" value={fmtNum(d.target_pps) ?? String(d.target_pps)} />
          )}
          {d.start_time && (
            <div className="col-span-2"><Field label="Started" value={fmtTime(d.start_time) ?? d.start_time} /></div>
          )}
          {d.stop_time && (
            <div className="col-span-2"><Field label="Stopped" value={fmtTime(d.stop_time) ?? d.stop_time} /></div>
          )}
        </Section>

        <ObservedReactionEditor
          engagementId={d.engagement_id ?? null}
          runId={d.run_id ?? d.id ?? null}
          initial={d.observed_reaction ?? d.observed ?? null}
          reviewedBy={d.reviewed_by ?? null}
          reviewedAt={d.reviewed_at ?? null}
          onSaved={(text) => {
            // patch the in-memory payload so re-opens see the new value
            // without waiting for a parent refetch
            d.observed_reaction = text
            d.observed = text
            onReactionSaved?.(d.run_id ?? d.id, text)
          }}
        />
      </BottomSheetShell>
    )
  }

  // synthetic "subnet" payload from SummaryView. distinct render path,
  // doesn't share fields with topology Host/Gateway/Self nodes
  if (d.kind === 'subnet') {
    return (
      <BottomSheetShell
        open
        onClose={onClose}
        ariaLabel={t('pentest.detail.subnet.title', 'Subnet details')}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Subnet</div>
            <div className="text-base font-semibold font-mono text-slate-800 truncate">{d.cidr}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
            aria-label={t('common.close', 'Close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <Section title="Overview" show>
          <div className="col-span-2">
            <Field label="CIDR" value={<span className="font-mono">{d.cidr}</span>} />
          </div>
          <Field label="Total hosts" value={String(d.hostCount ?? 0)} />
          {d.attackedCount > 0 && (
            <Field label="Attacked" value={<span className="text-rose-600 font-semibold">{d.attackedCount}</span>} />
          )}
          {d.highSevCount > 0 && (
            <Field label="High-severity" value={<span className="text-orange-600 font-semibold">{d.highSevCount}</span>} />
          )}
          {d.mediumSevCount > 0 && (
            <Field label="Medium-severity" value={<span className="text-amber-600 font-semibold">{d.mediumSevCount}</span>} />
          )}
        </Section>

        <Section title="Hosts" show={Array.isArray(d.hostIps) && d.hostIps.length > 0}>
          <div className="col-span-2 max-h-72 overflow-y-auto space-y-1">
            {(d.hostIps ?? []).map((ip: string) => (
              <div key={ip} className="font-mono text-[11px] text-slate-700 bg-slate-50 px-2 py-1 rounded">
                {ip}
              </div>
            ))}
          </div>
        </Section>
      </BottomSheetShell>
    )
  }

  // Identity (cytoscape host/gateway/self node payload)
  const ip       = d.ip ?? null
  const hostname = d.hostname ?? null
  const mac      = d.mac ?? d.mac_address ?? null
  const vendor   = d.vendor ?? null
  const nodeType = d.type ?? null
  const label    = d.label ?? null

  // device classification (Phase 2B/2C). prefer camelCase, fall back
  // to snake_case mirror
  const deviceType            = d.deviceType ?? d.device_type ?? null
  const deviceTypeConfidence  = d.deviceTypeConfidence ?? d.device_type_confidence ?? null
  const deviceVendor          = d.deviceVendor ?? d.device_vendor ?? null
  const deviceModel           = d.deviceModel ?? d.device_model ?? null
  const deviceFirmware        = d.deviceFirmwareVersion ?? d.device_firmware_version ?? null
  const deviceSerial          = d.deviceSerial ?? d.device_serial ?? null
  const hasDevice = deviceType || deviceVendor || deviceModel || deviceFirmware || deviceSerial

  // OS
  const osFamily     = d.osFamily ?? null
  const osConfidence = d.osConfidence ?? null
  const hasOs = osFamily || osConfidence

  // Probe
  const lastProbedAt   = d.lastProbedAt ?? null
  const lastProbeLevel = d.lastProbeLevel ?? null
  const openPortCount  = d.openPortCount ?? null
  const hasProbe = lastProbedAt || lastProbeLevel || openPortCount != null

  // SNMP
  const sysName  = d.sysName ?? null
  const sysDescr = d.sysDescr ?? null
  const hasSnmp = sysName || sysDescr

  // Subnet / topology
  const subnet       = d.subnet ?? d.network ?? null
  const parentSubnet = d.parentSubnet ?? d.parent ?? null
  const lastSeen     = d.lastSeen ?? null
  const stale        = d.stale === true || d.stale === 'true'
  const hasSubnet = subnet || parentSubnet || lastSeen != null

  // engagement-wide SNMP linkage for this host. match by asset_id (when
  // the payload carries one) or by host_ip, the same fallback the
  // server uses when linking findings/evidence on insert
  const hostSnmpEvidence = ip
    ? evidenceList.filter(ev => isSnmpEvidence(ev) && (
        (d.asset_id && ev.asset_id === d.asset_id) ||
        ev.asset?.host_ip === ip
      ))
    : []
  const hostSnmpFindings = ip
    ? findingsList.filter(f => isSnmpFinding(f, evidenceList) && (
        (d.asset_id && f.asset_id === d.asset_id) ||
        f.asset?.host_ip === ip
      ))
    : []
  const hasHostSnmp = hostSnmpEvidence.length > 0 || hostSnmpFindings.length > 0

  return (
    <BottomSheetShell
      open
      onClose={onClose}
      ariaLabel={t('pentest.detail.node.title', 'Node details')}
    >
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">
            {nodeType ?? t('pentest.detail.node.type', 'Node')}
          </div>
          <div className="text-base font-semibold text-slate-800 truncate" title={label ?? ip ?? ''}>
            {hostname || ip || label || t('pentest.detail.node.unnamed', '(unnamed)')}
            {ip && hostname && (
              <span className="ml-2 text-xs font-normal text-slate-500 font-mono">{ip}</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-slate-100 text-slate-500"
          aria-label={t('common.close', 'Close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Identity */}
      <Section title={t('pentest.detail.node.identity', 'Identity')} show={!!(ip || hostname || mac || vendor)}>
        {ip       && <Field label={t('pentest.detail.node.ip', 'IP')} value={<span className="font-mono">{ip}</span>} />}
        {hostname && <Field label={t('pentest.detail.node.hostname', 'Hostname')} value={hostname} />}
        {mac      && <Field label={t('pentest.detail.node.mac', 'MAC')} value={<span className="font-mono text-[11px]">{mac}</span>} />}
        {vendor   && <Field label={t('pentest.detail.node.vendor', 'Vendor (OUI)')} value={vendor} />}
      </Section>

      {/* Device classification */}
      <Section title={t('pentest.detail.node.deviceClass', 'Device classification')} show={!!hasDevice}>
        {deviceType && (
          <Field
            label={t('pentest.detail.node.deviceType', 'Type')}
            value={
              <span>
                {deviceType}
                {deviceTypeConfidence && (
                  <span className="text-slate-400 ml-1">({deviceTypeConfidence})</span>
                )}
              </span>
            }
          />
        )}
        {deviceVendor   && <Field label={t('pentest.detail.node.deviceVendor', 'Vendor')} value={deviceVendor} />}
        {deviceModel    && <Field label={t('pentest.detail.node.deviceModel', 'Model')} value={deviceModel} />}
        {deviceFirmware && <Field label={t('pentest.detail.node.deviceFirmware', 'Firmware')} value={deviceFirmware} />}
        {deviceSerial   && <Field label={t('pentest.detail.node.deviceSerial', 'Serial')} value={deviceSerial} />}
      </Section>

      {/* OS */}
      <Section title={t('pentest.detail.node.os', 'Operating system')} show={!!hasOs}>
        {osFamily     && <Field label={t('pentest.detail.node.osFamily', 'Family')} value={osFamily} />}
        {osConfidence && <Field label={t('pentest.detail.node.osConfidence', 'Confidence')} value={osConfidence} />}
      </Section>

      {/* Probe */}
      <Section title={t('pentest.detail.node.probe', 'Probe')} show={!!hasProbe}>
        {openPortCount != null && (
          <Field label={t('pentest.detail.node.openPorts', 'Open ports')} value={String(openPortCount)} />
        )}
        {lastProbeLevel && <Field label={t('pentest.detail.node.probeLevel', 'Level')} value={lastProbeLevel} />}
        {lastProbedAt && (
          <div className="col-span-2">
            <Field label={t('pentest.detail.node.lastProbed', 'Last probed')} value={fmtTime(lastProbedAt) ?? lastProbedAt} />
          </div>
        )}
      </Section>

      {/* SNMP topology-snapshot fields (legacy, from Dorothy node data) */}
      <Section title={t('pentest.detail.node.snmp', 'SNMP')} show={!!hasSnmp}>
        {sysName && (
          <div className="col-span-2">
            <Field label={t('pentest.detail.node.sysName', 'sysName')} value={sysName} />
          </div>
        )}
        {sysDescr && (
          <div className="col-span-2">
            <Field label={t('pentest.detail.node.sysDescr', 'sysDescr')} value={<span className="text-[11px]">{sysDescr}</span>} />
          </div>
        )}
      </Section>

      {/* linked SNMP evidence / findings (engagement_evidence + findings).
          surfaces the audit summary so the user sees the SNMP story
          directly on the host, with a button to jump to the evidence
          drawer for the full cards */}
      {hasHostSnmp && (
        <HostSnmpSummary
          evidence={hostSnmpEvidence}
          findings={hostSnmpFindings}
          onJumpToEvidence={onSelectEvidence}
        />
      )}

      {/* Subnet / topology */}
      <Section title={t('pentest.detail.node.subnet', 'Subnet')} show={!!hasSubnet}>
        {subnet       && <Field label={t('pentest.detail.node.subnetCidr', 'CIDR')} value={<span className="font-mono">{subnet}</span>} />}
        {parentSubnet && parentSubnet !== subnet && (
          <Field label={t('pentest.detail.node.parent', 'Parent')} value={<span className="font-mono">{parentSubnet}</span>} />
        )}
        {lastSeen != null && (
          <Field label={t('pentest.detail.node.lastSeen', 'Last seen')} value={fmtTime(new Date(Number(lastSeen)).toISOString()) ?? String(lastSeen)} />
        )}
        {stale && (
          <Field label={t('pentest.detail.node.stale', 'Stale')} value={<span className="text-amber-600">yes</span>} />
        )}
      </Section>
    </BottomSheetShell>
  )
}
